import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { PageContainer, toast } from "@outpave/ds";
import EmbeddedOriginDrawer from "components/platform/embedded/EmbeddedOriginDrawer";
import EmbeddedOriginsTable from "components/platform/embedded/EmbeddedOriginsTable";
import useEmbeddedOrigins from "hooks/useEmbeddedOrigins";
import { useNavBreadcrumbs } from "hooks/useNavBreadcrumbs";
import { toastMutationError } from "utils/toastMutationError";
import { MAX_EMBEDDED_ORIGINS } from "utils/embedded/embeddedOrigin";

/** The sites allowed to frame the platform's embedded components. */
const PlatformEmbeddedComponents: React.FC = () => {
  const { t } = useTranslation();
  const breadcrumbs = useNavBreadcrumbs(t("Embedded components"));
  const { origins, loading, error, saveOrigins } = useEmbeddedOrigins();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (error) setDrawerOpen(false);
  }, [error]);

  // Each handler reports a failure and rethrows, so its drawer or confirmation stays open to retry.
  const save = async (next: string[], success: string) => {
    try {
      await saveOrigins(next);
      toast.success(success);
    } catch (err: unknown) {
      toastMutationError(err, t("EmbeddedOriginsSaveError"));
      throw err;
    }
  };

  return (
    <PageContainer
      breadcrumbs={breadcrumbs}
      title={t("Embedded components")}
      subtitle={t("EmbeddedComponentsDescription")}
      primaryAction={
        <Button
          variant="primary-filled"
          onClick={() => setDrawerOpen(true)}
          disabled={
            loading || Boolean(error) || origins.length >= MAX_EMBEDDED_ORIGINS
          }
        >
          {t("Add origin")}
        </Button>
      }
    >
      <EmbeddedOriginsTable
        origins={origins}
        loading={loading}
        loadError={Boolean(error)}
        onRemove={(origin) =>
          save(
            origins.filter((allowed) => allowed !== origin),
            t("EmbeddedOriginRemoved"),
          )
        }
      />
      <EmbeddedOriginDrawer
        open={drawerOpen}
        allowed={origins}
        onClose={() => setDrawerOpen(false)}
        onAdd={(origin) => save([...origins, origin], t("EmbeddedOriginAdded"))}
      />
    </PageContainer>
  );
};

export default PlatformEmbeddedComponents;
